import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Animated, Image, Modal, StyleSheet, Text, View } from 'react-native';
import Svg, { Defs, LinearGradient, Rect, Stop } from 'react-native-svg';

import { JackpotChipPng, SmallVipSix } from '../../../assets';
import CloseIcon from '../../../assets/dailyMissions/close.svg';
import type { DonationContext } from '../../../services/donationsApi';
import { goldGlow } from '../../../shared/glowStyles';
import { navigationRef } from '../../../utils/helpers/navigationService';
import { useDevice } from '../../../utils/hooks/useDevice';
import { useSafeViewport } from '../../../utils/hooks/useSafeViewport';
import { useSvgId } from '../../../utils/hooks/useSvgId';
import { playSound } from '../../../utils/sound';
import { CtaButton } from '../common/CtaButton';
import { Pressable } from '../common/Touchable';
import { StatusGlyph } from './redesignPanelChrome';
import { useRedesignScale } from './useRedesignScale';

// The result popup in the checkout sheet's chrome (PaymentCheckoutModal): the red panel with the
// gold rim, a status badge, and for a finished purchase what was bought. Laid out on the 390-wide
// mobile frame and scaled to the viewport once.
const DESIGN_FRAME_WIDTH = 390;
const DESIGN_MODAL_WIDTH = 358;
const DESIGN_MODAL_HEIGHT = 380;
const MAX_SCALE = 1.15;
const VIEWPORT_MARGIN = 48;

const FONT = 'Sofia Sans';

const colors = {
	backdrop: 'rgba(0,0,0,0.68)',
	panelBorder: '#FFF908',
	panelBase: '#320400',
	surface: '#1A0603',
	white: '#FFFFFF',
	amount: '#FFE275',
	tileFill: '#330E07',
	tileBorder: '#DA600E',
	success: '#0CEB66',
	cancelled: '#FFC500',
	error: '#FF5A4A',
} as const;

const PANEL_FILL: Array<[string, string]> = [
	['0', '#770700'],
	['0.80127', '#320400'],
	['1', '#320400'],
];

interface PackageInfo {
	type: string;
	vip?: {
		durationDays: number;
	};
	chips?: number;
	chipAmount?: number;
	id?: string;
	name?: string;
}

interface PaymentStatusModalProps {
	visible: boolean;
	status: 'success' | 'cancelled' | 'error';
	packageInfo?: PackageInfo;
	onClose: () => void;
	autoDismiss?: boolean;
	autoDismissDelay?: number;
	/**
	 * Set for a donation: the success copy names the recipient and the now-unlocked chat, with a
	 * SEND MESSAGE action into it. It never auto-dismisses, so that action stays reachable.
	 */
	donation?: DonationContext;
}

export const PaymentStatusModal: React.FC<PaymentStatusModalProps> = ({
	visible,
	status,
	packageInfo,
	onClose,
	autoDismiss = true,
	autoDismissDelay = 5000,
	donation,
}) => {
	const { t } = useTranslation();
	const [scaleAnim] = useState(new Animated.Value(0));
	const { width, height } = useSafeViewport();
	const { isDesktop } = useDevice();
	const panelId = useSvgId('paymentStatusPanel');

	const desktopModalScale = useRedesignScale({ fitHeight: DESIGN_MODAL_HEIGHT });
	const scale = isDesktop
		? Math.min(desktopModalScale, (height - VIEWPORT_MARGIN) / DESIGN_MODAL_HEIGHT)
		: Math.min(
				width / DESIGN_FRAME_WIDTH,
				(height - VIEWPORT_MARGIN) / DESIGN_MODAL_HEIGHT,
				MAX_SCALE,
			);
	const styles = useMemo(() => createStyles(scale), [scale]);

	const handleClose = useCallback(() => {
		Animated.spring(scaleAnim, {
			toValue: 0,
			tension: 50,
			friction: 7,
			useNativeDriver: true,
		}).start(() => {
			onClose();
		});
	}, [onClose, scaleAnim]);

	useEffect(() => {
		if (visible) {
			Animated.spring(scaleAnim, {
				toValue: 1,
				tension: 50,
				friction: 7,
				useNativeDriver: true,
			}).start();

			// v1 shop.service plays CoinGain when the "successful coins" modal opens.
			if (status === 'success') playSound('CoinGain');

			if (autoDismiss && !donation && status === 'success') {
				const timer = setTimeout(() => {
					handleClose();
				}, autoDismissDelay);

				return () => clearTimeout(timer);
			}
		}
	}, [visible, autoDismiss, autoDismissDelay, donation, status, handleClose, scaleAnim]);

	const isSuccess = status === 'success';
	const isCancelled = status === 'cancelled';
	const isDonationSuccess = isSuccess && donation != null;

	// Close first (the store under it on native goes with it), then open the chat on top.
	const handleSendMessage = () => {
		if (!donation) return;
		const { recipientPlayerId, recipientUsername } = donation;
		handleClose();
		setTimeout(() => {
			if (!navigationRef.isReady()) return;
			navigationRef.navigate('PrivateChat', {
				playerId: recipientPlayerId,
				username: recipientUsername,
			});
		}, 0);
	};
	const statusColor = isSuccess ? colors.success : isCancelled ? colors.cancelled : colors.error;

	const getStatusTitle = () => {
		if (isDonationSuccess) {
			return t('T_SITE_DONATION_SUCCESS_TITLE', { defaultValue: 'Donation Successful!' });
		}
		switch (status) {
			case 'success':
				return t('T_SITE_PAYMENT_SUCCESS', { defaultValue: 'Payment Successful!' });
			case 'cancelled':
				return t('T_PAYMENT_CANCELLED', { defaultValue: 'Payment Cancelled' });
			case 'error':
				return t('T_PAYMENT_FAILED', { defaultValue: 'Payment Failed' });
			default:
				return t('T_SITE_PAYMENT_STATUS', { defaultValue: 'Payment Status' });
		}
	};

	const getStatusMessage = () => {
		if (donation && isDonationSuccess) {
			const amount = donation.chipAmount ?? packageInfo?.chipAmount ?? packageInfo?.chips ?? 0;
			const days = donation.vipDurationDays ?? packageInfo?.vip?.durationDays ?? 0;

			return donation.type === 'VIP'
				? t('T_SITE_DONATION_SUCCESS_VIP', {
						defaultValue:
							'Your VIP Donation to {{username}} was successful. You donated {{days}} days of VIP membership, and your private chat is now unlocked!',
						username: donation.recipientUsername,
						days,
					})
				: t('T_SITE_DONATION_SUCCESS_CHIPS', {
						defaultValue:
							'You successfully donated {{amount}} Chips to {{username}}. Your private chat is now unlocked!',
						username: donation.recipientUsername,
						amount: amount.toLocaleString(),
					});
		}
		switch (status) {
			case 'success':
				return t('T_SITE_PAYMENT_SUCCESS_MESSAGE', {
					defaultValue: 'Your purchase has been completed successfully.',
				});
			case 'cancelled':
				return t('T_SITE_PAYMENT_CANCELLED_MESSAGE', {
					defaultValue: 'The payment was not completed. Please try again.',
				});
			case 'error':
				return t('T_SITE_PAYMENT_ERROR_MESSAGE', {
					defaultValue: 'There was an error processing your payment. Please try again.',
				});
			default:
				return '';
		}
	};

	const formatPackageInfo = () => {
		if (!packageInfo) return null;

		if (packageInfo.type === 'VIP_MEMBERSHIP' && packageInfo.vip) {
			return {
				title: packageInfo.name || t('T_SITE_VIP_MEMBERSHIP', { defaultValue: 'VIP Membership' }),
				details: t('T_SITE_PAYMENT_VIP_DAYS', {
					defaultValue: '{{days}} Days',
					days: packageInfo.vip.durationDays,
				}),
				icon: SmallVipSix,
			};
		} else if (packageInfo.type === 'CHIPS') {
			const chips = packageInfo.chips || packageInfo.chipAmount || 0;
			return {
				title:
					packageInfo.name || t('T_SITE_PAYMENT_CHIPS_PACKAGE', { defaultValue: 'Chips Package' }),
				details: t('T_SITE_REFERRAL_REWARD_CHIPS', {
					defaultValue: '{{amount}} Chips',
					amount: chips.toLocaleString(),
				}),
				icon: JackpotChipPng,
			};
		} else if (packageInfo.type === 'Casino Points' || packageInfo.type === 'POINTS') {
			return {
				title: t('T_SITE_PAYMENT_CASINO_POINTS', { defaultValue: 'Casino Points' }),
				details: t('T_SITE_PAYMENT_ADDED_TO_WALLET', { defaultValue: 'Added to your wallet' }),
				icon: JackpotChipPng,
			};
		}
		return null;
	};

	const packageDetails = formatPackageInfo();

	return (
		<Modal
			visible={visible}
			transparent
			animationType='fade'
			onRequestClose={handleClose}
			statusBarTranslucent
			navigationBarTranslucent
		>
			<View style={styles.overlay}>
				<Pressable accessibilityRole='button' onPress={handleClose} style={styles.backdrop} />
				<Animated.View style={[styles.panel, { transform: [{ scale: scaleAnim }] }]}>
					<View pointerEvents='none' style={styles.panelFill}>
						<Svg width='100%' height='100%'>
							<Defs>
								<LinearGradient id={panelId} x1='0' y1='0' x2='0' y2='1'>
									{PANEL_FILL.map(([offset, color]) => (
										<Stop key={offset} offset={offset} stopColor={color} />
									))}
								</LinearGradient>
							</Defs>
							<Rect x='0' y='0' width='100%' height='100%' fill={`url(#${panelId})`} />
						</Svg>
					</View>

					<Pressable
						accessibilityRole='button'
						accessibilityLabel={t('T_SITE_CLOSE', { defaultValue: 'Close' })}
						onPress={handleClose}
						style={styles.closeHitArea}
						hitSlop={8}
					>
						<View style={styles.closeCircle}>
							<CloseIcon width={18 * scale} height={18 * scale} />
						</View>
					</Pressable>

					<View style={[styles.badge, { borderColor: statusColor, shadowColor: statusColor }]}>
						<StatusGlyph status={status} size={40 * scale} color={statusColor} />
					</View>

					<Text style={[styles.title, { color: statusColor }]}>{getStatusTitle()}</Text>
					<Text style={styles.message}>{getStatusMessage()}</Text>

					{isSuccess && packageDetails ? (
						<View style={styles.package}>
							<Image source={packageDetails.icon} style={styles.packageIcon} resizeMode='contain' />
							<View style={styles.packageText}>
								<Text numberOfLines={1} style={styles.packageTitle}>
									{packageDetails.title}
								</Text>
								<Text numberOfLines={1} style={styles.packageDetails}>
									{packageDetails.details}
								</Text>
							</View>
						</View>
					) : null}

					{isDonationSuccess ? (
						<CtaButton
							label={t('T_SITE_DONATION_SEND_MESSAGE', { defaultValue: 'SEND MESSAGE' })}
							variant='success'
							onPress={handleSendMessage}
							style={styles.action}
							labelStyle={styles.actionLabel}
						/>
					) : (
						<CtaButton
							label={
								isSuccess
									? t('T_SITE_PAYMENT_GREAT', { defaultValue: 'Great!' })
									: t('T_SITE_ACHIEVEMENTS_CLOSE', { defaultValue: 'Close' })
							}
							variant={isSuccess ? 'success' : 'gold'}
							onPress={handleClose}
							style={styles.action}
							labelStyle={styles.actionLabel}
						/>
					)}
				</Animated.View>
			</View>
		</Modal>
	);
};

const createStyles = (scale: number) => {
	const s = (value: number) => value * scale;

	return StyleSheet.create({
		overlay: {
			flex: 1,
			alignItems: 'center',
			justifyContent: 'center',
		},
		backdrop: {
			...StyleSheet.absoluteFillObject,
			backgroundColor: colors.backdrop,
		},
		panel: {
			width: s(DESIGN_MODAL_WIDTH),
			paddingHorizontal: s(20),
			paddingTop: s(28),
			paddingBottom: s(24),
			alignItems: 'center',
			borderRadius: s(16),
			borderWidth: Math.max(1, s(1.5)),
			borderColor: colors.panelBorder,
			// The gradient fill sits on top of this; the flat colour keeps the shadow layer opaque.
			backgroundColor: colors.panelBase,
			shadowColor: '#FFB800',
			shadowOffset: { width: 0, height: 0 },
			shadowOpacity: 0.45,
			shadowRadius: s(8),
			elevation: 12,
		},
		panelFill: {
			...StyleSheet.absoluteFillObject,
			borderRadius: s(16),
			overflow: 'hidden',
		},
		closeHitArea: {
			position: 'absolute',
			top: s(8),
			right: s(8),
			width: s(44),
			height: s(44),
			alignItems: 'center',
			justifyContent: 'center',
			zIndex: 2,
		},
		closeCircle: {
			width: s(32),
			height: s(32),
			borderRadius: s(16),
			borderWidth: Math.max(0.5, s(1)),
			borderColor: colors.panelBorder,
			backgroundColor: colors.surface,
			alignItems: 'center',
			justifyContent: 'center',
			...goldGlow,
		},
		badge: {
			width: s(76),
			height: s(76),
			borderRadius: s(38),
			borderWidth: Math.max(2, s(3)),
			backgroundColor: colors.surface,
			alignItems: 'center',
			justifyContent: 'center',
			marginBottom: s(16),
			shadowOffset: { width: 0, height: 0 },
			shadowOpacity: 0.6,
			shadowRadius: s(10),
			elevation: 8,
		},
		title: {
			fontFamily: FONT,
			fontSize: s(26),
			lineHeight: s(31),
			fontWeight: '800',
			textAlign: 'center',
			textShadowColor: 'rgba(0, 0, 0, 0.45)',
			textShadowOffset: { width: 0, height: 1 },
			textShadowRadius: 4,
			marginBottom: s(8),
		},
		message: {
			fontFamily: FONT,
			fontSize: s(15),
			lineHeight: s(20),
			fontWeight: '500',
			color: colors.white,
			opacity: 0.9,
			textAlign: 'center',
			marginBottom: s(20),
		},
		package: {
			alignSelf: 'stretch',
			flexDirection: 'row',
			alignItems: 'center',
			gap: s(12),
			padding: s(12),
			marginBottom: s(20),
			borderRadius: s(12),
			borderWidth: Math.max(0.5, s(1)),
			borderColor: colors.tileBorder,
			backgroundColor: colors.tileFill,
			shadowColor: '#FFB21A',
			shadowOffset: { width: 0, height: 0 },
			shadowOpacity: 0.55,
			shadowRadius: s(2.5),
			elevation: 3,
		},
		packageIcon: {
			width: s(40),
			height: s(40),
		},
		packageText: {
			flex: 1,
			gap: s(2),
		},
		packageTitle: {
			fontFamily: FONT,
			fontSize: s(16),
			lineHeight: s(19),
			fontWeight: '700',
			color: colors.amount,
		},
		packageDetails: {
			fontFamily: FONT,
			fontSize: s(14),
			lineHeight: s(17),
			fontWeight: '600',
			color: colors.white,
			opacity: 0.85,
		},
		action: {
			width: s(168),
			minHeight: s(40),
		},
		actionLabel: {
			fontSize: s(15),
		},
	});
};
